import type { OrderFormData } from "../components/DashBoard/Orders/common/Body";
import { OptionTimeframePayload } from "../components/DashBoard/GTT/gtt.helper";
import type {
  OrderDataFormerProbs,
  PreferencesResponse,
} from "../models/orders";
import type { OptionTimeframe } from "../components/DashBoard/GTT/gttTypes";

import { chopIntegerIntoArray } from "../chart-brokerapi/broker.helper";
import { remoteConfigGetter } from "./remoteConfig";

export type Variety = "RL" | "SL" | "SL-MKT" | "RL-MKT";

export const getFinalVariety = (
  isStopLoss: boolean,
  isMaret: boolean
): Variety => {
  if (isStopLoss) {
    if (isMaret) return "SL-MKT";
    else return "SL";
  } else {
    if (isMaret) return "RL-MKT";
    else return "RL";
  }
};

export const gttVarietyToTriggerType = (
  variety: OrderFormData["type"]
): Omit<Variety, "SL-MKT" | "SL"> => {
  switch (variety) {
    case "GTT-L":
      return "RL";
    case "GTT-M":
      return "RL-MKT";
    default:
      return ""; // Default return value, or you can return undefined if you want
  }
};

export const orderDataFormer = ({
  orderState,
  token,
  data,
}: OrderDataFormerProbs) => {
  const isGTT = data.type === "GTT-L" || data.type === "GTT-M";

  const finalData: any = {
    exchange: orderState.exchange,
    token: token,
    transaction_type: orderState.buySell,
    product: OptionTimeframePayload(orderState.tradeType),
    variety: getFinalVariety(data.isStopLoss, data.orderType === "Market"),
    quantity: +data.lots,
    price: data.orderType === "Market" ? 0 : +data.price,
    trigger_price: data.isStopLoss ? +data.triggerPrice : null,
    disclosed_quantity: 0, // In the future, we will work on this
    validity: data.validity, // Check if we have AMO and its case
    is_amo: data.validity === "AMO", // Check if we have AMO and its case
    order_identifier: null,
    tag_ids: null,
  };

  if (data.type === "Iceberg" && data.numberOfLegs) {
    finalData.legs = +data.numberOfLegs;
  }

  if (isGTT) {
    finalData.gtt = {
      // data.isGTTStopLoss ? +data.gttStoplossValue : nul
      sl_trigger_percent: data.isGTTStopLoss ? +data.gttStoplossValue : null,
      profit_trigger_percent: data.isGTTTarget ? +data.gttTargetValue : null,
      sl_variety: gttVarietyToTriggerType(data.type),
      profit_variety: gttVarietyToTriggerType(data.type),
    };
  }

  return finalData;
};

export const equityOrderPreferencesMapper = (
  equityPreference: PreferencesResponse["equityOrderPreferences"]
): OptionTimeframe => {
  switch (equityPreference) {
    case "delivery":
      return "Delivery";

    case "intraday":
      return "Intraday";

    case "mtf":
      return "MTF";

    case "t+5":
      return "T+5";

    default:
      return "Intraday";
  }
};

export const derivativeOrderPreferencesMapper = (
  derivativePreference: PreferencesResponse["derivativeOrderPreferences"]
): OptionTimeframe => {
  switch (derivativePreference) {
    case "delivery":
      return "CarryForward";

    case "intraday":
      return "Intraday";

    default:
      return "Intraday";
  }
};

export const getOrderByLots = (symbol: string, quantity: number): number[] => {
  // Handle the switch logic directly and return the result
  switch (symbol) {
    case "NIFTY":
      return chopIntegerIntoArray(
        Number(quantity),
        remoteConfigGetter("nifty50_max_qty").asNumber()
      );
    case "BANKNIFTY":
      return chopIntegerIntoArray(
        Number(quantity),
        remoteConfigGetter("banknifty_max_qty").asNumber()
      );
    case "FINNIFTY":
      return chopIntegerIntoArray(
        Number(quantity),
        remoteConfigGetter("finnifty_max_qty").asNumber()
      );

    case "NIFTYNXT50":
      return chopIntegerIntoArray(
        Number(quantity),
        remoteConfigGetter("nifty_next50_max_qty").asNumber()
      );
    case "SENSEX":
      return chopIntegerIntoArray(
        Number(quantity),
        remoteConfigGetter("sensex_max_qty").asNumber()
      );

    case "BANKEX":
      return chopIntegerIntoArray(
        Number(quantity),
        remoteConfigGetter("bankex_max_qty").asNumber()
      );
    case "MIDCPNIFTY":
      return chopIntegerIntoArray(
        Number(quantity),
        remoteConfigGetter("midcapnifty_max_qty").asNumber()
      );
    default:
      return [quantity]; // Default case when symbol doesn't match any of the above
  }
};

export const indexSymbolsForMarginReduction = [
  "NIFTY",
  "BANKNIFTY",
  "SENSEX",
  "FINNIFTY",
  "MIDCPNIFTY",
  "NIFTYNXT50",
  "BANKEX",
];
